import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { ArrowRight } from "lucide-react";

const HERO_SRC = "/hero-illustration.webp";
const HERO_ALT =
  "A marketing leader at a chaotic desk surrounded by falling papers, sticky-note questions, black spheres and Electric Blue light — the noise of modern B2B marketing.";
// Soft left-edge feather so the artwork melts into the page rather than sitting in a box.
const LEFT_FADE = {
  WebkitMaskImage:
    "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 7%, #000 22%)",
  maskImage:
    "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 7%, #000 22%)",
};

function Stats() {
  return (
    <div
      className="flex divide-x divide-neutral-200 border-t border-neutral-200 pt-8"
      data-testid="landing-stats"
    >
      {[
        ["4 min", "To complete"],
        ["24", "Diagnostic questions"],
        ["3", "Priorities that matter"],
      ].map(([n, l]) => (
        <div key={l} className="px-4 first:pl-0 sm:px-6">
          <div className="text-2xl font-semibold tracking-tight text-neutral-900">{n}</div>
          <div className="tw-eyebrow mt-1">{l}</div>
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <TopBar
        right={
          <button
            onClick={() => navigate("/admin")}
            className="tw-eyebrow hover:text-neutral-900 transition-colors"
            data-testid="landing-admin-link"
          >
            Admin
          </button>
        }
      />

      <section className="relative overflow-hidden" data-testid="landing-hero">
        {/* Desktop / laptop: full-bleed artwork occupying the right ~54% */}
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[54%] lg:block xl:w-[55%]">
          <img
            src={HERO_SRC}
            alt={HERO_ALT}
            className="h-full w-full select-none object-cover object-[38%_58%]"
            style={LEFT_FADE}
            draggable={false}
          />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="py-16 lg:w-[48%] lg:py-32 lg:pr-8">
            <p className="tw-eyebrow tw-fade-up" data-testid="landing-eyebrow">
              Three Words Full · B2B Marketing Diagnostic
            </p>
            <h1
              className="tw-fade-up mt-6 text-4xl font-bold leading-[1.04] tracking-tight text-neutral-900 sm:text-5xl lg:text-6xl"
              data-testid="landing-hero-headline"
            >
              What&apos;s holding your marketing back?
            </h1>
            <p className="tw-fade-up mt-6 max-w-xl text-lg leading-relaxed text-neutral-600">
              A four-minute diagnostic to identify what&apos;s working, what&apos;s getting in the
              way, and the three things that matter most.
            </p>

            <div className="tw-fade-up mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <button
                onClick={() => navigate("/pathway")}
                className="tw-btn-primary tw-focus inline-flex items-center gap-2 rounded-sm px-8 py-4 text-sm font-semibold uppercase tracking-wide"
                data-testid="landing-start-diagnostic-button"
              >
                Start the diagnostic
                <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-sm text-neutral-400">No account needed</span>
            </div>

            {/* Mobile / tablet: artwork stacks between the CTA and the statistics */}
            <div className="mt-12 lg:hidden" data-testid="landing-hero-artwork-mobile">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                className="h-auto w-full select-none"
                draggable={false}
              />
            </div>

            <div className="mt-12 lg:mt-16">
              <Stats />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
